import {
  OrderByRelationWithColumnAggregateFunction,
  QueryRelocationsOrderByColumn,
  QueryRelocationsOrderByRelationOrderByClause,
  QueryRelocationsOrderByVehicleColumn,
  QueryRelocationsWhereDeliveryCityColumn,
  QueryRelocationsWhereDepartureCityColumn,
  Region,
  RelocationListQueryVariables,
  RelocationStatus,
  RelocationType,
  SortOrder,
  SqlOperator,
} from "@/gql/graphql";
import {
  GetQueryVariablesFn,
  SortingState,
} from "@/lib/Components/DataTable/DataTable";

export type RelocationColumnId =
  | "select"
  | "status"
  | "relocation"
  | "inclusions"
  | "vehicle"
  | "expires_at"
  | "line"
  | "bookings"
  | "deal"
  | "deal_score"
  | "actions"
  | "created_at";

const sortMap: Partial<{
  [key in RelocationColumnId]: QueryRelocationsOrderByColumn;
}> = {
  status: QueryRelocationsOrderByColumn.Status,
  relocation: QueryRelocationsOrderByColumn.Id,
  expires_at: QueryRelocationsOrderByColumn.LatestDepartureDate,
  created_at: QueryRelocationsOrderByColumn.CreatedAt,
  deal_score: QueryRelocationsOrderByColumn.DealScore,
};

const relationSortColumns: RelocationColumnId[] = ["vehicle"];

// Shared by the table and its route loaders — both must request the same order
// or the preloaded query key won't match the component's.
export const relocationTableDefaultSorting: SortingState<RelocationColumnId> = [
  { id: "created_at", desc: true },
];

export function getRelocationTableVariables({
  supplierId,
  status,
  type,
  regions,
  departureCityIds,
  deliveryCityIds,
  queryVariables,
}: {
  supplierId: string | null;
  status: RelocationStatus[];
  type?: RelocationType[];
  regions?: Region[];
  departureCityIds?: string[];
  deliveryCityIds?: string[];
  queryVariables?: Partial<RelocationListQueryVariables>;
}): GetQueryVariablesFn<RelocationListQueryVariables, RelocationColumnId> {
  return ({ search, pagination, sorting }) => {
    if (search) {
      return {
        search,
        supplier_id: supplierId ?? undefined,
        first: pagination.pageSize,
        page: pagination.pageIndex,
        status: status.length ? status : undefined,
        type: type?.length ? type : undefined,
      };
    }

    const orderBy: QueryRelocationsOrderByRelationOrderByClause[] = sorting.map(
      (sort) => {
        const order = sort.desc ? SortOrder.Desc : SortOrder.Asc;

        if (relationSortColumns.includes(sort.id as RelocationColumnId)) {
          if (sort.id === "vehicle") {
            return {
              order,
              vehicle: {
                aggregate: OrderByRelationWithColumnAggregateFunction.Max,
                column: QueryRelocationsOrderByVehicleColumn.Name,
              },
            };
          }
        }

        const column = sortMap[sort.id as RelocationColumnId];

        if (!column) {
          console.log(`No sort configured for ${sort.id}`);
          return {
            column: QueryRelocationsOrderByColumn.CreatedAt,
            order: SortOrder.Desc,
          };
        }

        return {
          order,
          column,
        };
      },
    );

    return {
      supplier_id: supplierId ?? undefined,
      first: pagination.pageSize,
      page: pagination.pageIndex,
      status: status.length ? status : undefined,
      type: type?.length ? type : undefined,
      regions: regions?.length ? regions : undefined,
      whereDepartureCity: departureCityIds?.length
        ? {
            column: QueryRelocationsWhereDepartureCityColumn.Id,
            operator: SqlOperator.In,
            value: departureCityIds,
          }
        : undefined,
      whereDeliveryCity: deliveryCityIds?.length
        ? {
            column: QueryRelocationsWhereDeliveryCityColumn.Id,
            operator: SqlOperator.In,
            value: deliveryCityIds,
          }
        : undefined,
      is_linked: false,
      orderBy,
      ...queryVariables,
    } satisfies RelocationListQueryVariables;
  };
}
